// src/pages/BuySessionsPage.tsx

import React, { useEffect, useState, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, Link } from 'react-router-dom'
import { ArrowLeftIcon } from 'lucide-react'

import GlassPanel from '../components/GlassPanel'
import Button from '../components/Button'
import { paymentApi } from '../services/PaymentApi'
import { useTheme } from '../context/ThemeContext'
import { getAuthToken } from '../utils/auth'
import { getErrorMessage } from '../utils/errorHandler'
import { PurchaseBundleResponseDTO, PaymentResponseDTO, PaymentVerificationResult } from '../types/payment'

const P_KEY= process.env.REACT_APP_PAYMENT_GATEWAY_PKEY
// Add Moyasar types
declare global {
  interface Window {
    Moyasar: {
      init: (config: {
        element: string;
        amount: number;
        currency: string;
        description: string;
        publishable_api_key: string;
        callback_url: string;
        methods: string[];
        on_completed?: string | ((payment: any) => Promise<void>);
      }) => void;
    };
  }
}

const BuySessionsPage: React.FC = () => {
  const { theme } = useTheme();
  const [bundles, setBundles] = useState<any[]>([])
  const [selectedBundle, setSelectedBundle] = useState<any | null>(null)
  const [promoCode, setPromoCode] = useState('')
  const [promoFeedback, setPromoFeedback] = useState<string | null>(null)
  const [discountInfo, setDiscountInfo] = useState<{
    bundleId?: string
    discountPercentage: number
    freeExtraSession: boolean
  } | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [paymentFeedback, setPaymentFeedback] = useState<string | null>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [bundlesError, setBundlesError] = useState<string | null>(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [paymentDetails, setPaymentDetails] = useState<PurchaseBundleResponseDTO | null>(null)
  const [isPaymentCanceled, setIsPaymentCanceled] = useState(false)
  
  const paymentFormRef = useRef<HTMLDivElement>(null)

  const navigate = useNavigate()

  // Define handlePaymentVerification with useCallback before using it in useEffect
  const handlePaymentVerification = useCallback(async (paymentId: string, paymentGatewayId: string, status: string) => {
    setIsLoading(true);
    
    try {
      // Get the message from localStorage if available
      const message = localStorage.getItem('paymentMessage');
      
      // Create payment response DTO to send to backend
      const paymentResponseDTO: PaymentResponseDTO = {
        paymentId: paymentId,
        paymentGatewayId: paymentGatewayId,
        status: status,
        paymentGatewayMessage: message || undefined
      };
      
      // Call your backend to verify the payment
      const verificationResult: PaymentVerificationResult = await paymentApi.verifyPayment(paymentResponseDTO);
      
      // Reset payment form to show the bundle selection
      setShowPaymentForm(false);
      setPaymentDetails(null);
      
      if (status === 'paid') {
        setPaymentFeedback('✅ تم الدفع بنجاح');
        
        // Scroll to top to make sure the message is visible
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Redirect to team setup page after successful payment
        setTimeout(() => {
          // Clear all payment-related data from localStorage
          localStorage.removeItem('paymentDetails');
          localStorage.removeItem('paymentGatewayId');
          localStorage.removeItem('paymentStatus');
          localStorage.removeItem('paymentMessage');
          navigate('/team-setup');
        }, 2000);
      } else {
        const displayMessage = message || verificationResult.message || status;
        setPaymentFeedback(`❌ فشلت عملية الدفع: ${displayMessage}`);
        
        // Scroll to top to make sure the message is visible
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Payment verification failed:', error);
      
      // Improved error handling to extract the proper message from the API response
      let errorMessage = 'حدث خطأ غير متوقع';
      
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.data?.errors && axiosError.response.data.errors.length > 0) {
          // Extract the Arabic message from the API error response
          errorMessage = axiosError.response.data.errors[0].message.ar || errorMessage;
        } else if (axiosError.response?.data?.message) {
          // Fallback to general message if available
          errorMessage = axiosError.response.data.message;
        }
      } else {
        // Use the existing error handler as fallback
        errorMessage = getErrorMessage(error);
      }
      
      // Reset payment form to show the bundle selection
      setShowPaymentForm(false);
      setPaymentDetails(null);
      
      setPaymentFeedback(`❌ ${errorMessage}`);
      
      // Scroll to top to make sure the message is visible
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
      // Make sure URL params are cleared
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [navigate]); // Add navigate as dependency

  useEffect(() => {
    // Check if user is logged in
    const token = getAuthToken()
    setIsLoggedIn(!!token)
    
    // Fetch bundles regardless of login status
    const fetchBundles = async () => {
      try {
        const bundlesData = await paymentApi.getBundles()
        setBundles(bundlesData)
        setBundlesError(null)
      } catch (error) {
        console.error('Failed to fetch bundles:', error)
        setBundlesError(`فشل في تحميل الباقات: ${getErrorMessage(error)}`)
        setBundles([])
      }
    }
    
    fetchBundles()

    // Load Moyasar scripts
    const loadMoyasarScripts = () => {
      const scriptExists = document.querySelector('script[src="https://cdn.moyasar.com/mpf/1.15.0/moyasar.js"]')
      const styleExists = document.querySelector('link[href="https://cdn.moyasar.com/mpf/1.15.0/moyasar.css"]')
      
      if (!scriptExists) {
        const script = document.createElement('script')
        script.src = 'https://cdn.moyasar.com/mpf/1.15.0/moyasar.js'
        script.async = true
        document.head.appendChild(script)
      }
      
      if (!styleExists) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = 'https://cdn.moyasar.com/mpf/1.15.0/moyasar.css'
        document.head.appendChild(link)
      }
    }
    
    loadMoyasarScripts()

    // Check for payment status in URL for verification
    const urlParams = new URLSearchParams(window.location.search)
    const paymentGatewayId = urlParams.get('id')
    const paymentStatus = urlParams.get('status')
    const paymentMessage = urlParams.get('message')
    
    // If URL has payment info, store it in localStorage
    if (paymentGatewayId && paymentStatus) {
      localStorage.setItem('paymentGatewayId', paymentGatewayId)
      localStorage.setItem('paymentStatus', paymentStatus)
      if (paymentMessage) {
        localStorage.setItem('paymentMessage', paymentMessage)
      }
      
      // Clear the URL params
      window.history.replaceState({}, document.title, window.location.pathname)
    }
    
    // Check if we have both payment details and gateway info to verify payment
    const storedPaymentGatewayId = localStorage.getItem('paymentGatewayId')
    const storedPaymentStatus = localStorage.getItem('paymentStatus')
    const storedPaymentDetails = localStorage.getItem('paymentDetails')
    
    if (storedPaymentGatewayId && storedPaymentStatus && storedPaymentDetails) {
      const parsedPaymentDetails = JSON.parse(storedPaymentDetails)
      handlePaymentVerification(
        parsedPaymentDetails.paymentId,
        storedPaymentGatewayId,
        storedPaymentStatus
      )
      
      // Clear stored payment gateway info after verification
      localStorage.removeItem('paymentGatewayId')
      localStorage.removeItem('paymentStatus')
      // Message will be cleared in the handlePaymentVerification function
    }
  }, [handlePaymentVerification]) // Add handlePaymentVerification as dependency

  // Function to apply dark theme styles to Moyasar form
  const applyDarkThemeToMoyasarForm = useCallback(() => {
    // Wait a moment for the form to be fully rendered
    setTimeout(() => {
      // Get all Moyasar form elements that need styling
      const formContainer = document.querySelector('.mysr-form');
      if (!formContainer) return;
      
      // Add dark theme class to the container
      formContainer.classList.add('dark-theme-moyasar');
      
      // Add custom styles for dark theme
      const darkModeStyle = document.createElement('style');
      darkModeStyle.id = 'moyasar-dark-theme';
      darkModeStyle.textContent = `
        /* Moyasar Dark Theme Overrides */
        .dark-theme-moyasar {
          background-color: transparent !important;
          color: #E2E8F0 !important;
          border: none !important;
          box-shadow: none !important;
        }
        
        .dark-theme-moyasar .mysr-form-row {
          background-color: transparent !important;
          border-color: rgba(74, 85, 104, 0.5) !important;
          margin-bottom: 20px !important;
          border-radius: 8px !important;
          padding: 12px !important;
        }
        
        .dark-theme-moyasar input, 
        .dark-theme-moyasar select {
          background-color: rgba(26, 32, 44, 0.7) !important;
          color: #E2E8F0 !important;
          border-color: rgba(74, 85, 104, 0.5) !important;
          border-radius: 8px !important;
          padding: 12px !important;
          transition: all 0.2s ease !important;
        }
        
        .dark-theme-moyasar input:focus, 
        .dark-theme-moyasar select:focus {
          border-color: #00CEC9 !important;
          box-shadow: 0 0 0 2px rgba(0, 206, 201, 0.2) !important;
        }
        
        .dark-theme-moyasar input::placeholder {
          color: rgba(160, 174, 192, 0.7) !important;
        }
        
        .dark-theme-moyasar label,
        .dark-theme-moyasar .mysr-label {
          color: #A0AEC0 !important;
          margin-bottom: 8px !important;
          font-size: 0.9rem !important;
        }
        
        .dark-theme-moyasar .mysr-error {
          color: #FC8181 !important;
          margin-top: 4px !important;
          font-size: 0.85rem !important;
        }
        
        .dark-theme-moyasar .mysr-creditcard {
          background-color: rgba(26, 32, 44, 0.4) !important;
          border-radius: 12px !important;
          padding: 16px !important;
          border: 1px solid rgba(74, 85, 104, 0.3) !important;
        }
        
        .dark-theme-moyasar .mysr-creditcard-icon {
          filter: brightness(0.9) !important;
        }
        
        .dark-theme-moyasar .mysr-form-footer {
          background-color: transparent !important;
          padding-top: 10px !important;
          border-top: 1px solid rgba(74, 85, 104, 0.2) !important;
          margin-top: 10px !important;
        }
        
        .dark-theme-moyasar .mysr-security-badge {
          filter: grayscale(0.5) brightness(0.8);
          opacity: 0.7;
        }
        
        .dark-theme-moyasar button {
          background: linear-gradient(to right, #6C5CE7, #00CEC9) !important;
          border: none !important;
          color: white !important;
          border-radius: 8px !important;
          padding: 12px 24px !important;
          font-weight: 600 !important;
          transition: all 0.2s ease !important;
        }
        
        .dark-theme-moyasar button:hover {
          opacity: 0.9 !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }
      `;
      
      // Add or replace the style tag
      const existingStyle = document.getElementById('moyasar-dark-theme');
      if (existingStyle) {
        existingStyle.textContent = darkModeStyle.textContent;
      } else {
        document.head.appendChild(darkModeStyle);
      }
    }, 200);
  }, []);

  // Light theme style for Moyasar
  const applyLightThemeToMoyasarForm = useCallback(() => {
    setTimeout(() => {
      const formContainer = document.querySelector('.mysr-form');
      if (!formContainer) return;
      
      formContainer.classList.add('light-theme-moyasar');
      
      const lightModeStyle = document.createElement('style');
      lightModeStyle.id = 'moyasar-light-theme';
      lightModeStyle.textContent = `
        /* Moyasar Light Theme Overrides */
        .light-theme-moyasar {
          background-color: transparent !important;
          color: #2D3748 !important;
          border: none !important;
          box-shadow: none !important;
        }
        
        .light-theme-moyasar .mysr-form-row {
          background-color: transparent !important;
          border-color: rgba(226, 232, 240, 0.8) !important;
          margin-bottom: 20px !important;
          border-radius: 8px !important;
          padding: 12px !important;
        }
        
        .light-theme-moyasar input, 
        .light-theme-moyasar select {
          background-color: rgba(255, 255, 255, 0.8) !important;
          color: #2D3748 !important;
          border-color: rgba(203, 213, 224, 0.8) !important;
          border-radius: 8px !important;
          padding: 12px !important;
          transition: all 0.2s ease !important;
        }
        
        .light-theme-moyasar input:focus, 
        .light-theme-moyasar select:focus {
          border-color: #00CEC9 !important;
          box-shadow: 0 0 0 2px rgba(0, 206, 201, 0.15) !important;
        }
        
        .light-theme-moyasar input::placeholder {
          color: rgba(113, 128, 150, 0.7) !important;
        }
        
        .light-theme-moyasar label,
        .light-theme-moyasar .mysr-label {
          color: #4A5568 !important;
          margin-bottom: 8px !important;
          font-size: 0.9rem !important;
        }
        
        .light-theme-moyasar .mysr-error {
          color: #E53E3E !important;
          margin-top: 4px !important;
          font-size: 0.85rem !important;
        }
        
        .light-theme-moyasar .mysr-creditcard {
          background-color: rgba(255, 255, 255, 0.6) !important;
          border-radius: 12px !important;
          padding: 16px !important;
          border: 1px solid rgba(203, 213, 224, 0.8) !important;
        }
        
        .light-theme-moyasar .mysr-form-footer {
          background-color: transparent !important;
          padding-top: 10px !important;
          border-top: 1px solid rgba(203, 213, 224, 0.6) !important;
          margin-top: 10px !important;
        }
        
        .light-theme-moyasar button {
          background: linear-gradient(to right, #6C5CE7, #00CEC9) !important;
          border: none !important;
          color: white !important;
          border-radius: 8px !important;
          padding: 12px 24px !important;
          font-weight: 600 !important;
          transition: all 0.2s ease !important;
        }
        
        .light-theme-moyasar button:hover {
          opacity: 0.9 !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
        }
      `;
      
      const existingStyle = document.getElementById('moyasar-light-theme');
      if (existingStyle) {
        existingStyle.textContent = lightModeStyle.textContent;
      } else {
        document.head.appendChild(lightModeStyle);
      }
    }, 200);
  }, []);

  // Update the useEffect to apply the right theme
  useEffect(() => {
    // Initialize payment form when payment details are available
    if (showPaymentForm && paymentDetails && paymentFormRef.current) {
      const initPaymentForm = () => {
        if (!window.Moyasar) {
          setTimeout(initPaymentForm, 500) // Retry if Moyasar is not loaded yet
          return
        }
        
        // Convert amount to smallest currency unit (e.g., halala for SAR)
        const amountInSmallestUnit = Math.round(paymentDetails.amount * 100)
        
        window.Moyasar.init({
          element: '.mysr-form',
          amount: amountInSmallestUnit,
          currency: paymentDetails.currency,
          description: paymentDetails.description,
          publishable_api_key: P_KEY || '',
          callback_url: `${window.location.origin}${window.location.pathname}`,
          methods: ['creditcard'],
          on_completed: async function(payment) {
            console.log('Payment completed:', payment)
          }
        })
        
        // Apply theme styling
        if (theme === 'dark') {
          applyDarkThemeToMoyasarForm();
        } else {
          applyLightThemeToMoyasarForm();
        }
      }
      
      initPaymentForm()
    }
  }, [showPaymentForm, paymentDetails, theme, applyDarkThemeToMoyasarForm, applyLightThemeToMoyasarForm])

  // Remove custom styles when component unmounts
  useEffect(() => {
    return () => {
      const darkModeStyle = document.getElementById('moyasar-dark-theme');
      if (darkModeStyle) {
        darkModeStyle.remove();
      }
      const lightModeStyle = document.getElementById('moyasar-light-theme');
      if (lightModeStyle) {
        lightModeStyle.remove();
      }
    };
  }, []);

  // Store payment details in localStorage to persist across page reloads
  useEffect(() => {
    // On component mount, check if we have stored payment details
    const storedPaymentDetails = localStorage.getItem('paymentDetails')
    if (storedPaymentDetails && !paymentDetails) {
      setPaymentDetails(JSON.parse(storedPaymentDetails))
    }
  }, [paymentDetails])

  // Update localStorage when payment details change
  useEffect(() => {
    if (paymentDetails) {
      localStorage.setItem('paymentDetails', JSON.stringify(paymentDetails))
    }
  }, [paymentDetails])

  // Reset the canceled flag when payment details change
  useEffect(() => {
    if (paymentDetails) {
      setIsPaymentCanceled(false);
    }
  }, [paymentDetails]);

  const handlePromoValidate = async () => {
    if (!isLoggedIn) {
      setPromoFeedback('❌ يجب تسجيل الدخول لاستخدام رمز الخصم')
      return
    }
    
    try {
      const response = await paymentApi.validatePromoCode(
        promoCode,
        selectedBundle?.bundleId
      )
      setPromoFeedback(
        `✅ خصم ${response.discountPercentage * 100}%${
          response.freeExtraSession ? ' + جلسة مجانية' : ''
        }`
      )
      setDiscountInfo({
        bundleId: response.bundleId,
        discountPercentage: response.discountPercentage,
        freeExtraSession: response.freeExtraSession,
      })
    } catch (error) {
      // Improved error handling to extract the proper message from the API response
      let errorMessage = 'حدث خطأ غير متوقع';
      
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.data?.errors && axiosError.response.data.errors.length > 0) {
          // Extract the Arabic message from the API error response
          errorMessage = axiosError.response.data.errors[0].message.ar || errorMessage;
        } else if (axiosError.response?.data?.message) {
          // Fallback to general message if available
          errorMessage = axiosError.response.data.message;
        }
      } else {
        // Use the existing error handler as fallback
        errorMessage = getErrorMessage(error);
      }
      
      setPromoFeedback(`❌ ${errorMessage}`);
      setDiscountInfo(null);
    }
  }

  const handlePurchase = async () => {
    if (!selectedBundle) return;
    
    if (!isLoggedIn) {
      setPaymentFeedback('❌ يجب تسجيل الدخول لشراء الجلسات')
      return
    }

    setIsLoading(true);
    setPaymentFeedback(null);
    
    try {
      // Get payment details from API
      const response: PurchaseBundleResponseDTO = await paymentApi.PurchaseBundle(
        selectedBundle.bundleId,
        promoCode || undefined
      );
      
      // Set payment details and show payment form
      setPaymentDetails(response);
      setShowPaymentForm(true);
      setPaymentFeedback(null);
    } catch (error) {
      console.error('Failed to initialize payment:', error);
      
      // Improved error handling to extract the proper message from the API response
      let errorMessage = 'حدث خطأ غير متوقع';
      
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.data?.errors && axiosError.response.data.errors.length > 0) {
          // Extract the Arabic message from the API error response
          errorMessage = axiosError.response.data.errors[0].message.ar || errorMessage;
        } else if (axiosError.response?.data?.message) {
          // Fallback to general message if available
          errorMessage = axiosError.response.data.message;
        }
      } else {
        // Use the existing error handler as fallback
        errorMessage = getErrorMessage(error);
      }
      
      setPaymentFeedback(`❌ ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelPayment = useCallback(() => {
    if (paymentDetails?.paymentId && !isPaymentCanceled) {
      try {
        // Call the API to cancel the payment
        paymentApi.cancelPayment(paymentDetails.paymentId);
        console.log('Payment cancelled:', paymentDetails.paymentId);
        // Set the flag to prevent duplicate calls
        setIsPaymentCanceled(true);
      } catch (error) {
        console.error('Error cancelling payment:', error);
      }
    }
    
    setShowPaymentForm(false);
    setPaymentDetails(null);
    // Clear payment details from localStorage
    localStorage.removeItem('paymentDetails');
  }, [paymentDetails, isPaymentCanceled]);

  // Add event listener for the beforeunload event to handle page close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (showPaymentForm && paymentDetails?.paymentId && !isPaymentCanceled) {
        try {
          // Using navigator.sendBeacon for a more reliable way to send the request before the page is unloaded
          const url = `${process.env.REACT_APP_API_URL || ''}/api/payments/cancel?paymentId=${paymentDetails.paymentId}`;
          navigator.sendBeacon(url);
          setIsPaymentCanceled(true);
        } catch (error) {
          console.error('Error sending payment cancellation beacon:', error);
        }
      }
    };

    // Add the event listener if we're showing the payment form
    if (showPaymentForm && paymentDetails?.paymentId) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    
    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [showPaymentForm, paymentDetails, isPaymentCanceled]);

  // Add an effect to cancel the payment if the component unmounts with an active payment
  useEffect(() => {
    // This will run when the component unmounts
    return () => {
      if (showPaymentForm && paymentDetails?.paymentId && !isPaymentCanceled) {
        try {
          // Try to cancel the payment when the component unmounts
          paymentApi.cancelPayment(paymentDetails.paymentId);
          console.log('Payment cancelled on unmount:', paymentDetails.paymentId);
        } catch (error) {
          console.error('Error cancelling payment on unmount:', error);
        }
      }
    };
  }, [showPaymentForm, paymentDetails, isPaymentCanceled]);

  const calculateDiscountedPrice = (price: number) => {
    return discountInfo
      ? (price * (1 - discountInfo.discountPercentage)).toFixed(2)
      : price.toFixed(2)
  }

  return (
    <div
      className={`fixed inset-0 w-full h-full overflow-auto ${
        theme === 'dark' 
          ? 'bg-gradient-to-br from-[#2D3436] to-[#0D0F1A]' 
          : 'bg-gradient-to-br from-[#F7F9FB] to-[#D6E4FF]'
      }`}
    >
      {/* Floating shapes - kept but adjusted positioning */}
      <motion.div
        className={`fixed top-[10%] left-[5%] w-32 h-32 rounded-full z-0 opacity-30 ${
          theme === 'dark' ? 'bg-[#4B7BFF]/20' : 'bg-[#4B7BFF]/20'
        }`}
        animate={{ y: [0, 25, 0], x: [0, -15, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className={`fixed bottom-[15%] right-[5%] w-40 h-40 rounded-full z-0 opacity-30 ${
          theme === 'dark' ? 'bg-[#8E44AD]/20' : 'bg-[#8E44AD]/20'
        }`}
        animate={{ y: [0, -20, 0], x: [0, 20, 0] }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1,
        }}
      />

      {/* Top navigation bar */}
      <div className="sticky top-0 z-30 backdrop-blur-sm bg-opacity-70 p-4 flex justify-between items-center border-b border-opacity-20 mb-2 bg-inherit">
        <Link to="/">
          <Button
            variant="secondary"
            className="px-4 py-2 flex items-center gap-2"
            icon={<ArrowLeftIcon size={16} />}
          >
            رجوع
          </Button>
        </Link>
        <h1 className={`text-xl md:text-3xl font-bold ${
          theme === 'dark' 
            ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#6C5CE7] to-[#00CEC9]' 
            : 'text-transparent bg-clip-text bg-gradient-to-r from-[#6C5CE7] to-[#00CEC9]'
        }`}>
          شراء جلسات إضافية
        </h1>
        <div className="w-[84px]"></div> {/* Empty div for balance */}
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pb-10 z-10 relative">
        {/* Display payment feedback prominently at the top */}
        {paymentFeedback && (
          <div className={`p-4 rounded-lg border text-center mb-6 font-medium max-w-2xl mx-auto ${
            paymentFeedback.startsWith('✅')
              ? theme === 'dark'
                ? 'bg-green-500/20 text-green-300 border-green-500/30'
                : 'bg-green-100 text-green-700 border-green-200'
              : theme === 'dark'
                ? 'bg-red-500/20 text-red-300 border-red-500/30'
                : 'bg-red-100 text-red-700 border-red-200'
          }`}>
            {paymentFeedback}
          </div>
        )}

        {!isLoggedIn && (
          <div className={`p-4 rounded-lg text-center mb-6 max-w-2xl mx-auto ${
            theme === 'dark' 
              ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' 
              : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
          }`}>
            يجب تسجيل الدخول لاستخدام رمز الخصم وشراء الجلسات. 
            <Link to="/login" className="underline font-medium mr-2">
              تسجيل الدخول
            </Link>
          </div>
        )}

        {bundlesError && (
          <div className={`p-4 rounded-lg text-center mb-6 max-w-2xl mx-auto ${
            theme === 'dark' 
              ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}>
            {bundlesError}
          </div>
        )}

        {!showPaymentForm ? (
          <div className="flex flex-col lg:flex-row gap-6 justify-center items-start">
            {/* Bundle selection and promo code */}
            <GlassPanel variant="elevated" className="p-6 space-y-6 w-full lg:w-7/12">
              {/* Promo Code */}
              <div className="space-y-4">
                <h2 className={`text-xl font-bold ${
                  theme === 'dark' ? 'text-[#00CEC9]' : 'text-[#00CEC9]'
                }`}>
                  رمز الخصم
                </h2>
                <div className="flex flex-col sm:flex-row gap-3 items-center">
                  <input
                    type="text"
                    placeholder="أدخل رمز الخصم (إن وجد)"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    disabled={!isLoggedIn}
                    className={`flex-1 px-4 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-[#00CEC9] ${
                      !isLoggedIn ? 'opacity-60 cursor-not-allowed' : ''
                    } ${
                      theme === 'dark'
                        ? 'bg-[#2D3748] text-white border-[#4A5568]'
                        : 'bg-white text-[#2D3748] border-[#E8ECEF]'
                    }`}
                  />
                  <Button 
                    variant="secondary" 
                    onClick={handlePromoValidate}
                    disabled={!isLoggedIn}
                  >
                    تحقق من الرمز
                  </Button>
                </div>
                {promoFeedback && (
                  <p
                    className={`text-sm ${
                      promoFeedback.startsWith('✅')
                        ? theme === 'dark'
                          ? 'text-green-400'
                          : 'text-green-600'
                        : theme === 'dark'
                          ? 'text-red-400'
                          : 'text-red-600'
                    }`}
                  >
                    {promoFeedback}
                  </p>
                )}
              </div>

              {/* Bundle Options */}
              <div className="space-y-6">
                <h2 className={`text-xl font-bold ${
                  theme === 'dark' ? 'text-[#00CEC9]' : 'text-[#00CEC9]'
                }`}>
                  اختر الباقة
                </h2>
                <p className={`text-base mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  الدفع عن طريق مدى، ابل باي، فيزا، وماستركارد.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
                  {bundles.map((bundle) => (
                    <motion.div
                      key={bundle.bundleId}
                      whileHover={{ y: -5, scale: 1.03 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      className={`relative overflow-hidden rounded-xl cursor-pointer shadow-md ${
                        selectedBundle?.bundleId === bundle.bundleId
                          ? theme === 'dark' 
                            ? 'ring-2 ring-[#00CEC9] shadow-[0_0_15px_rgba(0,206,201,0.5)]' 
                            : 'ring-2 ring-[#00CEC9] shadow-[0_0_15px_rgba(0,206,201,0.3)]'
                          : ''
                      }`}
                      onClick={() => {
                        setSelectedBundle(bundle)
                        setPromoFeedback(null)
                      }}
                    >
                      <div className={`absolute inset-0 ${
                        theme === 'dark' 
                          ? 'bg-gradient-to-br from-[#1A202C] to-[#2D3748]' 
                          : 'bg-gradient-to-br from-white to-[#EDF2F7]'
                      } opacity-90`}></div>
                      
                      <div className={`absolute top-0 right-0 left-0 h-1.5 ${
                        selectedBundle?.bundleId === bundle.bundleId
                          ? 'bg-gradient-to-r from-[#6C5CE7] to-[#00CEC9]'
                          : 'bg-gradient-to-r from-[#6C5CE7] to-[#00CEC9] opacity-50'
                      }`}></div>
                      
                      <div className="relative p-6 flex flex-col items-center justify-between min-h-[160px]">
                        <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-20 ${
                          theme === 'dark' ? 'bg-[#6C5CE7]' : 'bg-[#6C5CE7]'
                        }`}></div>
                        
                        <div className="text-center mb-2 w-full">
                          <div className={`text-5xl font-extrabold mb-1 ${
                            theme === 'dark' 
                              ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#6C5CE7] to-[#00CEC9]' 
                              : 'text-transparent bg-clip-text bg-gradient-to-r from-[#6C5CE7] to-[#00CEC9]'
                          }`}>
                            {bundle.quantity}
                          </div>
                          <div className={`text-lg font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            {bundle.quantity === 1 ? 'جلسة' : 'جلسات'}
                          </div>
                          {bundle.bundleName && bundle.bundleName !== `${bundle.quantity} جلسة` && (
                            <div className={`text-xs mt-1 ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {bundle.bundleName}
                            </div>
                          )}
                        </div>
                        
                        <div className={`mt-4 px-5 py-2 rounded-full ${
                          theme === 'dark' 
                            ? 'bg-[#2D3748] text-white' 
                            : 'bg-white text-[#2D3748] shadow-sm'
                        }`}>
                          <span className="font-bold text-lg">{bundle.price}</span>
                          <span className="mr-1">ريال</span>
                        </div>
                      </div>
                      
                      {selectedBundle?.bundleId === bundle.bundleId && (
                        <div className={`absolute bottom-0 left-0 right-0 h-1.5 ${
                          theme === 'dark'
                            ? 'bg-gradient-to-r from-[#00CEC9] to-[#6C5CE7]'
                            : 'bg-gradient-to-r from-[#00CEC9] to-[#6C5CE7]'
                        }`}></div>
                      )}

                      {/* Best Value Tag for the 10 sessions bundle */}
                      {bundle.quantity >= 10 && (
                        <div className="absolute top-0 left-0 bg-gradient-to-r from-[#6C5CE7] to-[#00CEC9] text-white text-xs py-1 px-3 rounded-bl-lg rounded-tr-lg font-bold transform -translate-y-0 translate-x-0">
                          قيمة أفضل
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </GlassPanel>

            {/* Payment Summary */}
            {selectedBundle && (
              <GlassPanel variant="elevated" className="p-6 lg:sticky lg:top-24 w-full lg:w-5/12">
                <div className="space-y-4">
                  <h2 className={`text-xl font-bold mb-4 ${
                    theme === 'dark' ? 'text-[#00CEC9]' : 'text-[#00CEC9]'
                  }`}>
                    تفاصيل الشراء
                  </h2>
                  <div className="flex justify-between text-base">
                    <span>الباقة:</span>
                    <span className="font-medium">{selectedBundle.bundleName}</span>
                  </div>
                  <div className="flex justify-between text-base">
                    <span>عدد الجلسات:</span>
                    <span className="font-medium">{selectedBundle.quantity}</span>
                  </div>
                  <div className="flex justify-between text-base">
                    <span>السعر الأصلي:</span>
                    <span className="font-medium">{selectedBundle.price} ريال</span>
                  </div>
                  {discountInfo && (
                    <>
                      <div className={`flex justify-between text-base ${
                        theme === 'dark' ? 'text-green-400' : 'text-green-600'
                      }`}>
                        <span>نسبة الخصم:</span>
                        <span className="font-medium">
                          {(discountInfo.discountPercentage * 100).toFixed(0)}%
                        </span>
                      </div>
                      {discountInfo.freeExtraSession && (
                        <div className={`flex justify-between text-base ${
                          theme === 'dark' ? 'text-green-400' : 'text-green-600'
                        }`}>
                          <span>جلسة مجانية:</span>
                          <span className="font-medium">1</span>
                        </div>
                      )}
                      <div className={`flex justify-between font-bold text-lg mt-2 ${
                        theme === 'dark' ? 'text-[#00CEC9]' : 'text-[#00CEC9]'
                      }`}>
                        <span>السعر بعد الخصم:</span>
                        <span>
                          {calculateDiscountedPrice(selectedBundle.price)} ريال
                        </span>
                      </div>
                    </>
                  )}
                  <Button 
                    variant="primary" 
                    className="w-full mt-6 py-3 text-base" 
                    onClick={handlePurchase}
                    disabled={isLoading}
                  >
                    {isLoading ? 'جاري التحميل...' : 'إتمام الشراء'}
                  </Button>
                  <div className={`text-center mt-2 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <div className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </GlassPanel>
            )}
          </div>
        ) : (
          /* Completely revamped Moyasar Payment Form */
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className={`rounded-2xl overflow-hidden ${
                theme === 'dark' 
                  ? 'bg-gradient-to-br from-[#1A202C]/60 to-[#2D3748]/60 backdrop-blur-md border border-[#4A5568]/30' 
                  : 'bg-gradient-to-br from-white/80 to-[#F7FAFC]/80 backdrop-blur-md border border-[#E2E8F0]/60'
              } shadow-xl`}
            >
              <div className="p-6 md:p-8">
                {/* Header with product info */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 pb-6 border-b border-opacity-20">
                  <div>
                    <h2 className={`text-2xl font-bold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-800'
                    }`}>
                      إتمام عملية الدفع
                    </h2>
                    <p className={`mt-1 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {selectedBundle?.bundleName} - {selectedBundle?.quantity} جلسة
                    </p>
                  </div>
                  <div className={`mt-4 md:mt-0 text-right ${
                    theme === 'dark' ? 'text-white' : 'text-gray-800'
                  }`}>
                    <p className="text-sm opacity-70">المبلغ المستحق</p>
                    <p className="text-2xl font-bold">
                      {discountInfo 
                        ? calculateDiscountedPrice(selectedBundle?.price || 0) 
                        : selectedBundle?.price} ريال
                    </p>
                    {discountInfo && (
                      <p className={`text-sm line-through opacity-60 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {selectedBundle?.price} ريال
                      </p>
                    )}
                  </div>
                </div>
              
                {/* Payment form container */}
                <div className={`rounded-xl p-1 ${
                  theme === 'dark' 
                    ? 'bg-gradient-to-r from-[#6C5CE7]/20 to-[#00CEC9]/20'
                    : 'bg-gradient-to-r from-[#6C5CE7]/10 to-[#00CEC9]/10'
                }`}>
                  <div className={`rounded-lg overflow-hidden p-4 ${
                    theme === 'dark' ? 'bg-[#1A202C]/80' : 'bg-white/80'
                  }`}>
                    <div 
                      ref={paymentFormRef}
                      className={`mysr-form ${theme === 'dark' ? 'dark-theme-moyasar' : 'light-theme-moyasar'}`}
                    ></div>
                  </div>
                </div>
              
                {/* Action buttons */}
                <div className="mt-8 flex justify-between items-center">
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className="inline-block mr-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </span>
                    جميع المعاملات آمنة ومشفرة
                  </p>
                  <Button 
                    variant="secondary" 
                    className="px-6 py-2 bg-opacity-40" 
                    onClick={cancelPayment}
                  >
                    إلغاء الدفع
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuySessionsPage;